import { useCallback, useEffect, useRef } from 'react';
import { useQontoWebsocket, useQontoWebsocketConnection } from '@repo/shared-kit/websocket';
import { useConfig } from '@repo/shared-kit/config';

export const CONTRACT_SIGN_COMPLETED = 'contract_sign_completed';
export const CONTRACT_SIGN_VALIDATION_ERROR = 'contract_sign_validation_error';

export interface UseContractSignWebSocketOptions {
  token: string;
  userId: string;
  onSuccess?: () => void;
  onValidationError?: () => void;
  onTimeout?: () => void;
  timeoutEnabled: boolean;
}

export function useContractSignWebSocket({
  token,
  userId,
  onSuccess,
  onValidationError,
  onTimeout,
  timeoutEnabled,
}: UseContractSignWebSocketOptions) {
  const timeoutRef = useRef<number | null>(null);

  const { urls } = useConfig();

  useQontoWebsocketConnection({
    websocketBaseURL: urls.websocketBaseURL,
    token,
    userId,
  });

  const clearTimeoutIfExists = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const handleSuccess = useCallback(() => {
    clearTimeoutIfExists();
    onSuccess?.();
  }, [onSuccess, clearTimeoutIfExists]);

  const handleValidationError = useCallback(() => {
    clearTimeoutIfExists();
    onValidationError?.();
  }, [onValidationError, clearTimeoutIfExists]);

  useQontoWebsocket(CONTRACT_SIGN_COMPLETED, handleSuccess);
  useQontoWebsocket(CONTRACT_SIGN_VALIDATION_ERROR, handleValidationError);

  useEffect(() => {
    if (timeoutEnabled) {
      timeoutRef.current = window.setTimeout(() => {
        onTimeout?.();
      }, 4000);
    }

    return () => {
      clearTimeoutIfExists();
    };
  }, [timeoutEnabled, onTimeout, clearTimeoutIfExists]);
}
